import * as React from 'react';
import Container from 'containers/Container';
import WebinarPreview from 'components/WebinarPreview';
import ControlButton from 'components/ControlButton';
import {ButtonVariant} from 'components/Button';
import {IconClip, IconCopy, IconEdit, IconTrash} from 'icons';
import useLoading from '../hooks/use-loading';
import {WebinarObject} from '../api/webinar/types';
import {getWebinars} from '../api/webinar/requests';
import validateResponse from '../helpers/validate-response';
import isError from '../helpers/isError';
import HeaderSearch from '../components/HeaderSearch';
import ButtonLink from '../components/ButtonLink';
import Header from '../components/Header';
import styled from "styled-components";
import {Filter} from '../components/Filter';
import {CourseStatus} from '../api/course';
import Radio from '../components/Radio';
import {FolderSelector} from '../components/FolderSelector';
import {Helmet} from 'react-helmet';

const Wrapper = styled.div`
  margin-bottom: 120px;
`

export const Filters = styled.div`
  margin-top: 20px;
  justify-content: space-between;
  display: flex;
`;

export const StyledSelector = styled.div`
  margin-bottom: 14px;
`;

/**
 * The webinars page
 * @constructor
 */
export default function Webinars(): React.ReactElement {
  const {data, fetch, render} = useLoading<WebinarObject[]>();

  React.useEffect(() => {
    fetch(() => new Promise((resolve) => (
      getWebinars({
        my: '1',
      }).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          resolve(response);
        }
      })
    )));
  }, []);

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    return (
      <Wrapper>
        <Header>
          <HeaderSearch
            inputSearch={{
              placeholder: 'Поиск по названию трансляции',
            }}
            renderAfter={(
              <ButtonLink href="/webinars/create" variant={ButtonVariant.Primary}>
                Создать трансляцию
              </ButtonLink>
            )}
          />
          <Filters>
            <Filter controls>
              <Filter.Group name="status" label="Публикация">
                {(group) => (
                  [
                    (
                      <Filter.Field
                        type="radio"
                        label="Опубликовано"
                        value={CourseStatus.Public}>
                        {(field) => (
                          <Radio
                            name={group.name}
                            value={field.value}
                            checked={field.checked}
                            onChange={field.onChange}
                            label="Опубликовано"
                          />
                        )}
                      </Filter.Field>
                    ),
                    (
                      <Filter.Field
                        type="radio"
                        label="Не опубликовано"
                        value={CourseStatus.Draft}>
                        {(field) => (
                          <Radio
                            name={group.name}
                            value={field.value}
                            checked={field.checked}
                            onChange={field.onChange}
                            label="Не опубликовано"
                          />
                        )}
                      </Filter.Field>
                    ),
                  ]
                )}
              </Filter.Group>
              <Filter.Group name="createdAt" label="Дата создания">
                {(group) => (
                  [
                    (
                      <Filter.Field
                        type="radio"
                        label="Сегодня"
                        value="today">
                        {(field) => (
                          <Radio
                            name={group.name}
                            value={field.value}
                            checked={field.checked}
                            onChange={field.onChange}
                            label="Сегодня"
                          />
                        )}
                      </Filter.Field>
                    ),
                    (
                      <Filter.Field
                        type="radio"
                        label="Вчера"
                        value="tomorrow">
                        {(field) => (
                          <Radio
                            name={group.name}
                            value={field.value}
                            checked={field.checked}
                            onChange={field.onChange}
                            label="Вчера"
                          />
                        )}
                      </Filter.Field>
                    ),
                  ]
                )}
              </Filter.Group>
            </Filter>
          </Filters>
        </Header>
        <Container>
          <StyledSelector>
            <FolderSelector type="webinars" />
          </StyledSelector>
          {data?.map((webinar, index) => (
            <WebinarPreview
              key={index}
              {...webinar}
              renderControls={(
                <React.Fragment>
                  <ControlButton>
                    <IconClip />
                  </ControlButton>
                  <ControlButton>
                    <IconEdit />
                  </ControlButton>
                  <ControlButton>
                    <IconCopy />
                  </ControlButton>
                  <ControlButton>
                    <IconTrash />
                  </ControlButton>
                </React.Fragment>
              )}
            />
          ))}
        </Container>
      </Wrapper>
    );
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <div>
        Not found
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>GrowPlace / Все трансляции</title>
      </Helmet>
      {render(renderContent, renderNotFound)}
    </React.Fragment>
  );
}
